import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { QuizModuleService } from "../../quiz-module.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ColumnMode } from "@swimlane/ngx-datatable";
import { UserLeague } from "@core/models/user-league";
import { User } from "@core/models";
import { AuthenticationService } from "@core/services/auth";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { LeaderboardInfoPageComponent } from "../leaderboard-info-page/leaderboard-info-page.component";
import { Reward } from "@core/models/reward";

@Component({
  standalone: false,
  selector: "app-leaderboards",
  templateUrl: "./leaderboards.component.html",
  styleUrls: ["./leaderboards.component.scss"],
})
export class LeaderboardsComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();
  userLeagueDetails: UserLeague;
  leagueMembers: UserLeague[];
  currentUser: User;
  currentPage = 1;
  currentOffset = 0;
  pageSize = 10;
  dataTableLeagueData: UserLeague[];
  rankingLeagueData: UserLeague[];
  isLoading = true;
  ColumnMode: any = ColumnMode;
  ngbAlertHeading: string;
  ngbAlertMessage: string;
  alertType: string;
  currentUserName: string;
  leagueLevels = ["Bronze", "Silver", "Gold", "Platinum"];
  currentLeagueIndex: number;
  userId: number;
  rewards: Reward[];
  leaderboardPeriod = "week";
  active = 1;
  totalLeagueMembers: UserLeague[] = []; // Store all members for sorting
  currentLeagueId: number;
  currentLeagueType: string;
  totalCount: number = 0; // Total number of league members

  constructor(
    private quizService: QuizModuleService,
    private authenticationService: AuthenticationService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    this.currentUserName = this.currentUser.name.split(" ")[0];
    this.getUserLeague();
  }

  getUserLeague(): void {
    this.quizService
      .getUserLeague()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          this.userLeagueDetails = res;
          this.userId = this.userLeagueDetails.userId;
          this.getLeagueMembers(res.leagueId, res.type);
          this.currentLeagueIndex = this.leagueLevels.indexOf(
            this.userLeagueDetails.leagueLevelTitle
          );
        },
        (err) => {
          console.log(err);
        }
      );
  }

  getLeagueMembers(leagueId: number, leagueType: string): void {
    this.isLoading = true;
    this.currentLeagueId = leagueId;
    this.currentLeagueType = leagueType;
    this.quizService
      .getLeagueMembers(leagueId, leagueType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          this.isLoading = false;
          this.leagueMembers = res;
          this.totalLeagueMembers = res;
          this.totalCount = res.length; // Store total count
          this.dataTableLeagueData = this.leagueMembers.sort((a, b) => b.weekPoints - a.weekPoints);
          this.rankingLeagueData = this.dataTableLeagueData;
          this.customizeUserMessage();
        },
        (err) => {
          this.isLoading = false;
          console.log(err);
        }
      );
  }

  fetchRequests(event): void {
    console.log('Pagination event:', event);
    console.log('Current page:', this.currentPage, 'Page size:', this.pageSize);
    console.log('Total count:', this.totalCount);
    
    this.isLoading = true;
    this.currentPage = event.offset + 1;
    this.currentOffset = event.offset;
    this.pageSize = event.pageSize;

    // Use server-side pagination
    this.quizService
      .getLeagueMembersPaginated(this.currentLeagueId, this.currentLeagueType, this.currentPage, this.pageSize)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          console.log('API response length:', res.length);
          this.isLoading = false;
          
          // Check if we got paginated data or all data (fallback)
          if (res.length <= this.pageSize && this.currentPage > 1) {
            // This might be paginated data, use as is
            this.leagueMembers = res;
            console.log('Using server-side pagination');
          } else {
            // This is likely all data (fallback), do client-side pagination
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            this.leagueMembers = res.slice(start, end);
            console.log('Using client-side pagination fallback, slice from', start, 'to', end);
          }
          
          console.log('Final league members length:', this.leagueMembers.length);
          
          // Update the data table with current page data
          if (this.leaderboardPeriod === "week") {
            this.dataTableLeagueData = this.leagueMembers.sort((a, b) => b.weekPoints - a.weekPoints);
          } else {
            this.dataTableLeagueData = this.leagueMembers.sort((a, b) => b.pointsToday - a.pointsToday);
          }
          this.rankingLeagueData = this.dataTableLeagueData;
        },
        (err) => {
          this.isLoading = false;
          console.error('Error fetching paginated league members:', err);
        }
      );
  }

  getRowClass = (row) => {
    const index = this.dataTableLeagueData.indexOf(row);
    if (index < 3 && row.weekPoints > 0) {
      return "green-row";
    } else if (index >= this.dataTableLeagueData.length - 3) {
      return "red-row";
    }
    return "";
  };

  showMoreInfo(): void {
    this.modalService.open(LeaderboardInfoPageComponent, {
      centered: true,
      size: "lg",
    });
  }

  customizeUserMessage(): void {
    const userPosition = this.userLeagueDetails.position;
    const user = this.leagueMembers.find(
      (user) => user.userId === this.userLeagueDetails.userId
    );
    const userIndex = this.leagueMembers?.findIndex(
      (student) => student.userId === user.userId
    );

    if (this.leagueMembers?.length > 29) {
      if (userPosition <= 3) this.showPromotionMessage();
      if (userPosition > 3 && userPosition <= 6) this.closeToTopMessage();
      if (userPosition > 6 && userPosition <= 24) this.midPositionMessage();
      if (userPosition > 24 && userPosition <= 27) this.closeToBottomMessage();
      if (userPosition > 27) this.showDemotionMessage();
    }

    if (this.leagueMembers?.length < 29) {
      if (userIndex < 3) {
        this.showPromotionMessage();
      } else if (userIndex >= 3 && userIndex < this.leagueMembers.length - 3) {
        this.midPositionMessage();
      } else {
        this.showDemotionMessage();
      }
    }
  }

  showPromotionMessage(): void {
    const nextLeagueForPromotion =
      this.currentLeagueIndex < this.leagueLevels.length - 1
        ? this.leagueLevels[this.currentLeagueIndex + 1]
        : null;
    this.ngbAlertHeading = `Way to go ${this.currentUserName}🥳`;
    this.ngbAlertMessage = nextLeagueForPromotion
      ? `You are in the top 3! Keep it up and you'll be promoted to the ${nextLeagueForPromotion} league.`
      : "You are in the top 3! Keep it up";
    this.alertType = "info";
  }

  closeToTopMessage(): void {
    const pointsToEarn =
      this.leagueMembers[2].totalPoints -
      this.userLeagueDetails.totalPoints +
      1;
    this.ngbAlertHeading = `You're so close to the top ${this.currentUserName}👏`;
    this.ngbAlertMessage =
      pointsToEarn > 1
        ? `Earn ${pointsToEarn} more points to reach number 3 and stand a chance to get to the promotion zone🌟.`
        : `Earn ${pointsToEarn} more point to reach number 3 and stand a chance to get to the promotion zone🌟.`;
    this.alertType = "info";
  }

  midPositionMessage(): void {
    this.ngbAlertHeading = `Keep going ${this.currentUserName}💪`;
    this.ngbAlertMessage =
      "Continue earning points to get closer to the top! Every point you earn matters.";
    this.alertType = "info";
  }

  closeToBottomMessage(): void {
    const pointsToEarn =
      this.leagueMembers[22].totalPoints -
      this.userLeagueDetails.totalPoints +
      1;
    this.ngbAlertHeading = `Be Careful ${this.currentUserName}⚠️`;
    this.ngbAlertMessage =
      pointsToEarn > 0
        ? `You are close to the bottom 3. Earn ${pointsToEarn} more points to secure your position and avoid being placed in the demotion zone.`
        : `You are close to the bottom 3. Earn ${pointsToEarn} more point to secure your position and avoid being placed in the demotion zone.`;
    this.alertType = "closeToBottom";
  }

  showDemotionMessage(): void {
    const pointsToEarn =
      this.leagueMembers.length > 29
        ? this.leagueMembers[26].totalPoints -
          this.userLeagueDetails.totalPoints +
          1
        : this.leagueMembers[2].totalPoints -
          this.userLeagueDetails.totalPoints +
          1;
    const nextDemotionLeague =
      this.currentLeagueIndex > 0
        ? this.leagueLevels[this.currentLeagueIndex - 1]
        : null;
    this.ngbAlertHeading = "Warning‼️";
    this.ngbAlertMessage = nextDemotionLeague
      ? `You are in the bottom 3 ${this.currentUserName}. Earn ${pointsToEarn} more points by watching more video lessons and attempt quizzes in order to avoid being demoted to the ${nextDemotionLeague} league.`
      : `You are in the bottom 3 ${this.currentUserName}.Earn more points by watching more video lessons and attempting more quizzes in order to move up the leaderboard.`;
    this.alertType = "danger";
  }

  leaderboardSelectChange(event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.leaderboardPeriod = selectElement.value;
    
    // Reset to first page when changing period
    this.currentPage = 1;
    
    // Fetch data for the new period
    this.quizService
      .getLeagueMembersPaginated(this.currentLeagueId, this.currentLeagueType, this.currentPage, this.pageSize)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          // Check if we got paginated data or all data (fallback)
          if (res.length <= this.pageSize) {
            // This might be paginated data, use as is
            this.leagueMembers = res;
          } else {
            // This is likely all data (fallback), do client-side pagination
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            this.leagueMembers = res.slice(start, end);
          }
          
          if (this.leaderboardPeriod === "week") {
            this.dataTableLeagueData = this.leagueMembers.sort((a, b) => b.weekPoints - a.weekPoints);
          } else {
            this.dataTableLeagueData = this.leagueMembers.sort((a, b) => b.pointsToday - a.pointsToday);
          }
          this.rankingLeagueData = this.dataTableLeagueData;
        },
        (err) => {
          console.error('Error fetching league members for period change:', err);
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
